import {
  Badge,
  confirm,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { EditIcon, TrashIcon } from "lucide-react";
import { RowActions } from "@/shared/ui/RowActions";
import type { InstallmentPlanTemplateDTO } from "../../domain/contracts";

type InstallmentPlanTemplateTableProps = {
  data: InstallmentPlanTemplateDTO[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onEdit: (row: InstallmentPlanTemplateDTO) => void;
  onDelete: (id: string) => void;
};

export function InstallmentPlanTemplateTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onEdit,
  onDelete,
}: InstallmentPlanTemplateTableProps) {
  const columns: TanstackReactTable.ColumnDef<InstallmentPlanTemplateDTO>[] = [
    createSortableColumn("name", "ຊື່ແມ່ແບບ"),
    {
      id: "rows",
      header: "ງວດ",
      cell: ({ row }) => {
        const summary = row.original.rows
          .map((r) => `${r.percent}%`)
          .join(" / ");
        return (
          <span className="text-sm">
            {row.original.rows.length} ງວດ
            {summary ? (
              <span className="text-muted-foreground"> · {summary}</span>
            ) : null}
          </span>
        );
      },
    },
    {
      id: "isDefault",
      header: "ເລີ່ມຕົ້ນ",
      cell: ({ row }) =>
        row.original.isDefault ? (
          <Badge variant="default">ເລີ່ມຕົ້ນ</Badge>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      id: "status",
      header: "ສະຖານະ",
      cell: ({ row }) => {
        const active = row.original.status === "active";
        return (
          <Badge variant={active ? "default" : "secondary"}>
            {active ? "ໃຊ້ງານ" : "ບໍ່ໃຊ້ງານ"}
          </Badge>
        );
      },
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <RowActions
          actions={[
            {
              label: "ແກ້ໄຂ",
              icon: <EditIcon className="h-4 w-4" />,
              onClick: () => onEdit(row.original),
            },
            {
              label: "ລຶບ",
              icon: <TrashIcon className="h-4 w-4" />,
              variant: "destructive",
              onClick: async () => {
                const ok = await confirm({
                  title: "ລຶບແມ່ແບບແຜນງວດ",
                  description:
                    "ແມ່ແບບນີ້ຈະຖືກລຶບ — ແຜນງວດໃນໃບເກັບເງິນທີ່ສ້າງແລ້ວບໍ່ກະທົບ",
                  actionText: "ລຶບ",
                  ActionProps: { variant: "destructive" },
                });
                if (ok) onDelete(row.original.id);
              },
            },
          ]}
        />
      ),
    },
  ];

  return (
    <DataTable<InstallmentPlanTemplateDTO, unknown>
      noDataMessage="ບໍ່ພົບແມ່ແບບແຜນງວດ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
    />
  );
}
